import jwt_decode from "jwt-decode";
import { User } from "./types";
import {
  InvalidIssuerError,
  InvalidUserError,
  ExpiredTokenError,
  InvalidTokenError,
  MissingTokenError,
} from "../errors";

export const isAuthenticated = () =>
  getAccessToken() !== null && getIdToken() !== null;

export const getAccessToken = () => sessionStorage.getItem("token");

export const getIdToken = () => sessionStorage.getItem("id_token");

export const getValidatedUser = (): User => {
  const user = getUser();

  const expiryTimestamp = user.exp;

  if (isExpired(expiryTimestamp)) {
    const formattedDate = formatExpiryDate(expiryTimestamp);
    throw new ExpiredTokenError(`token expired on ${formattedDate}`);
  }

  const issuer = user.iss;
  if (!isIssuedByGsk(issuer)) {
    throw new InvalidIssuerError(
      `token issuer ${issuer} is invalid - must be issued by gsk.com`
    );
  }

  return user;
};

export const getCurrentTime = (): number => {
  const currentUnixTimestamp = new Date().getTime() / 1000;
  return Math.floor(currentUnixTimestamp);
};

export const getExpiryTimeAsString = (
  expire_period: number | undefined
): string => {
  const definedExpirePeriod = expire_period ? expire_period : 0;
  return `${getCurrentTime() + definedExpirePeriod}`;
};

export const isExpired = (tokenExpiryTime: number): boolean => {
  const currentTime = getCurrentTime();
  return currentTime > tokenExpiryTime;
};

const isIssuedByGsk = (issuer: string): boolean => {
  return issuer.endsWith(".gsk.com");
};

const formatExpiryDate = (expiryTimestamp: number): string => {
  const date = new Date(expiryTimestamp * 1000);

  return date.toLocaleString();
};

const isUser = (unvalidatedUser: User): unvalidatedUser is User => {
  const userFields = ["iss", "aud", "sub", "exp"];

  for (let i = 0; i < userFields.length; i++) {
    let field = userFields[i];
    if (!unvalidatedUser.hasOwnProperty(field)) {
      return false;
    }
  }

  return true;
};

export const getUser = (): User => {
  let decodedToken: User;
  const token = getIdToken();

  if (!token) throw new MissingTokenError("token not found in session storage");

  try {
    decodedToken = jwt_decode<User>(token);
  } catch (error: unknown) {
    throw new InvalidTokenError("token is not valid auth token");
  }
  if (isUser(decodedToken)) {
    return decodedToken as User;
  }
  throw new InvalidUserError("token is not valid user object");
};

export const logout = () => {
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("refresh_token");
  sessionStorage.removeItem("expiry");
  return window.location.reload();
};
